import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getComicsByCategory, getCategoryBySlug, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || '');
  const categoryComics = getComicsByCategory(slug || '');

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${currentCategory.name}韩漫_${currentCategory.name}漫画在线免费阅读`;
  const pageDescription = `${currentCategory.description}。韩国漫画网提供大量${currentCategory.name}类韩漫免费在线阅读，每日更新。`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${currentCategory.name}韩漫,${currentCategory.name}漫画,韩国漫画,韩漫免费阅读`}
        canonicalUrl={`/category/${slug}`}
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: pageDescription,
            url: `https://www.koreancomic.cn/category/${slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.koreancomic.cn', name: '首页' } },
                { '@type': 'ListItem', position: 2, item: { '@id': `https://www.koreancomic.cn/category/${slug}`, name: currentCategory.name } },
              ],
            },
          },
        ]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              {currentCategory.name}韩漫
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-8 pb-8 border-b border-background-200/70">
            <span className="text-sm text-foreground-400 mr-2">切换分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-xl mb-3 aspect-[3/4] bg-background-200">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - ${currentCategory.name}韩漫`}
                      title={`${comic.title} - 韩国漫画网${currentCategory.name}分类`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-md">
                        {comic.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1">
                    {comic.author} · {comic.status}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-book-2-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无漫画内容，敬请期待</p>
              <Link to="/" className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700">
                返回首页浏览其他分类
              </Link>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}